import React, {useEffect, useRef, useState} from 'react';
import magnifier from "../../../icons/maginifier.svg";
import "./styles.scss";
import {FocusableField, TypeAheadSearchFocusProps} from "./props";
import {TypeaheadSearch} from "../../index";
import closeGrey from '../../../icons/close_grey2.svg';
import {toValidChar} from "../../../utils/input";

function EmptyDisplayedBox({focusTypeAhead, emptyPlaceholder = 'SEARCH'}: FocusableField) {
  const [isHovered, setIsHovered] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isHovered) {
      wrapperRef.current?.focus();
    } else {
      wrapperRef.current?.blur();
    }
  }, [isHovered]);
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isHovered && document.activeElement === wrapperRef.current) {
      const passed = toValidChar(e.key);
      focusTypeAhead(passed);
    }
  };
  const onClickEmptyTextField = () => {
    focusTypeAhead();
  }

  return (
    <div className="empty-box__container" onClick={onClickEmptyTextField}>
      <div className="focused-box__wrapper"
        tabIndex={0}
        ref={wrapperRef}
        contentEditable={isHovered}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onKeyDown={handleKeyPress}
        suppressContentEditableWarning={true}
      >
        <span className="focused-box__icon">
          <img
            src={magnifier}
            alt="Search Icon"
            className="focused-box__icon-image"
          />
        </span>
        <span className="focused-box__text">{emptyPlaceholder}</span>
      </div>
    </div>
  );
}

function FocusSearchInput(props: TypeAheadSearchFocusProps) {
  const {minCharacters, placeholder, notFoundText, focus, onSearchChange, changeFocus, onKeyUp, hideSuggestions = true, options = []} = props;
  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (['Escape', 'Enter'].includes(e.key)) {
      changeFocus?.(false);
    }
    onKeyUp?.(e);
  }
  const handleSearch = (value: string) => {
    onSearchChange?.(value);
    changeFocus?.(false);
  }
  return (
    <div className="focused-box__container">
      <div className="focused-box__wrapper">
        <TypeaheadSearch
          minCharacters={minCharacters}
          options={options}
          search={props.search}
          placeholder={placeholder}
          notFoundText={notFoundText}
          closeIcon={closeGrey}
          hideSearchIcon={false}
          hideClearButton={false}
          hideSuggestions={hideSuggestions}
          onKeyUp={handleKeyUp}
          focus={focus}
          inputType="search"
          role="searchbox"
          enterKeyHint="search"
          onSearchChange={handleSearch}
        />
      </div>
    </div>
  );
}

function TypeAheadSearchFocus(props: TypeAheadSearchFocusProps) {
  const {focus, onFocusChange} = props;
  const [searchFocus, setSearchFocus] = useState(!!focus);
  const [char, setChar] = useState<string | undefined>('');
  useEffect(() => {
    setSearchFocus(!!focus);
  }, [focus]);
  useEffect(() => {
    onFocusChange?.(searchFocus);
  }, [searchFocus]);
  const showTypeAhead = (char?: string) => {
    setChar(char);
    // we need to delay a little the focus otherwise the event is fired in the input
    setTimeout(() => {
      setSearchFocus(true);
    }, 0);
  }
  const searchChangeFocus = (newFocus: boolean) => setSearchFocus(newFocus);
  return searchFocus ?
    <FocusSearchInput {...props} search={char} changeFocus={searchChangeFocus} focus={searchFocus} /> :
    <EmptyDisplayedBox focusTypeAhead={showTypeAhead} emptyPlaceholder={props?.emptyPlaceholder} />;
}

export { EmptyDisplayedBox };
export default React.memo(TypeAheadSearchFocus);
